import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DateSelect, PageHeader, Spinner } from '../../../Components'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { useCapsule } from '../../../State/Functions'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { columnProps } from '../../Prompts/Functions'

const language = cap.language.state || 'IT'
const t = translations[language]

const patchConversation = async (id) => {
  try {
    const res = await FC.service('conversations').patch(id, { status: 'ended' })
    invalidateQuery(['conversation', 'conversations'])
    showToast('success', t.success, t.conversazioneChiusa)
    return res
  } catch (e) {
    showToast('error', t.errore, e?.message || t.erroreChiusuraConversazione)
    return false
  }
}

/**
 * Pagina admin lista conversazioni con filtro data e azioni rapide.
 * @returns {JSX.Element} Componente lista conversazioni admin
 */
export function ConversationsList() {
  const [selectedDate, setSelectedDate] = useCapsule(cap.selectedDateConversationList)
  const { data: conversations = [], isSuccess } = useCQuery(['conversations', selectedDate])
  const [conversationsTable, setConversationsTable] = useState([])
  const navigate = useNavigate()

  useEffect(() => {
    isSuccess &&
      setConversationsTable(
        conversations.map((r) => ({ ...r, createdAt: new Date(r.createdAt).toLocaleString() }))
      )
  }, [conversations, isSuccess])

  if (!isSuccess) return <Spinner />

  return (
    <>
      <PageHeader
        title={t.listaConversazioni}
        subtitle={t.gestioneConversazioniPerData}
        actions={
          <button type="button" className="nbtn" onClick={() => navigate('/process/performance')}>
            <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
            {t.tornaIndietro}
          </button>
        }
      />

      <div className="ncard" style={{ marginBottom: 14 }}>
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-filter" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.filters}</h3>
            <div className="ncard__sub">{t.filtraPerData}</div>
          </div>
        </div>
        <div className="ncard__body">
          <DateSelect date={[selectedDate, setSelectedDate]} label="" />
        </div>
      </div>

      <div className="ncard" style={{ padding: 0 }}>
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-comments" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.conversations}</h3>
          </div>
        </div>
        <DataTable
          style={{ width: '100%' }}
          filterDisplay="row"
          sortField="createdAt"
          sortOrder={-1}
          className="TableSignatures ntable-datatable"
          value={conversationsTable}
          emptyMessage={t.noProcessFound}
          responsiveLayout="scroll"
          paginator
          rows={10}
        >
          <Column field="id" {...columnProps(t.id)} />
          <Column
            field="travellerId"
            {...columnProps(t.viaggiatore)}
            body={({ traveller: { email = '', phone = '' } = {} }) =>
              [email, phone]?.filter((e) => e).join(' - ')
            }
          />
          <Column field="status" {...columnProps(t.state)} />
          <Column field="createdAt" {...columnProps(t.creationDate)} />
          <Column
            headerStyle={{ visibility: 'hidden' }}
            style={{ border: 'none', width: 80, textAlign: 'center' }}
            body={({ id, status }) => (
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                <button
                  type="button"
                  className="naction-btn naction-btn--primary"
                  title={t.open}
                  onClick={() => navigate('/conversations/' + id)}
                >
                  <i className="pi pi-eye" style={{ fontSize: 15 }} />
                </button>
                {status === 'open' && (
                  <button
                    type="button"
                    className="naction-btn"
                    title={t.chiudi}
                    style={{ color: 'var(--danger)' }}
                    onClick={() => patchConversation(id)}
                  >
                    <i className="pi pi-times" style={{ fontSize: 15 }} />
                  </button>
                )}
              </div>
            )}
          />
        </DataTable>
      </div>
    </>
  )
}
